import { useEffect } from "react";
import * as Sentry from "@sentry/react";
import { useOnboarding } from "~/OnboardingApp/hooks/useOnboarding";
import { useLazyOnboardingIndexQuery } from "~/store/api";
import { useAppDispatch, useAppSelector } from "~/store/hooks";
import { setHasLoadedInitial } from "~/store/onboarding";
import { useCurrentUser } from "./useCurrentUser";

interface UseLoadOnboarding {
  hasLoadedInitial: boolean;
}

export const useLoadOnboarding = (): UseLoadOnboarding => {
  const user = useCurrentUser();
  const hasLoadedOnboardingInitial = useAppSelector(
    (state) => state.onboarding.hasLoadedInitial,
  );
  const hasLoadedUserInitial = useAppSelector(
    (state) => state.authentication.hasLoadedInitial,
  );

  const dispatch = useAppDispatch();
  const [apiOnboardingIndex] = useLazyOnboardingIndexQuery();
  const { handleSetOnboarding } = useOnboarding();

  useEffect(() => {
    if (!hasLoadedUserInitial || hasLoadedOnboardingInitial) {
      return;
    }

    if (!user) {
      dispatch(setHasLoadedInitial(true));
      return;
    }

    (async () => {
      try {
        const responseBody = await apiOnboardingIndex().unwrap();
        const onboarding = responseBody.data.attributes;

        handleSetOnboarding(onboarding);
      } catch (err: any) {
        if (err?.status === 401) {
          Sentry.captureException(err);
        }
      } finally {
        dispatch(setHasLoadedInitial(true));
      }
    })();
  }, [
    apiOnboardingIndex,
    dispatch,
    handleSetOnboarding,
    hasLoadedOnboardingInitial,
    hasLoadedUserInitial,
    user,
  ]);

  return { hasLoadedInitial: hasLoadedOnboardingInitial };
};
